import React, { useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosContext } from '../contextManager/axios.context';
import { useUser } from '../contextManager/user.context';
import { useToast } from '../contextManager/toast.context';
import { useScale } from './HeightScaler';
import { endpoints } from '../constants/endpoints';
import { styled } from '../styles/stitches.config';
import { PageShell } from '../styles/profile/layout.style';
import { Dialog, Overlay } from '../styles/search/search.style';
import { TutorDetailView } from '../components/search/TutorDetailView';
import { UserDetails } from '../types/schemas/abc';
import { AuthSkyline } from '../components/common/AuthSkyline';
import {
  CheckCircleFill,
  XCircleFill,
  EyeFill,
  FileEarmarkTextFill,
  FilePersonFill,
  ExclamationTriangleFill,
} from 'react-bootstrap-icons';
import { ClipLoader } from 'react-spinners';

/* Shape returned by GET /api/admin/tutors — a user row plus review metadata */
interface AdminTutor extends UserDetails {
  missingFields: string[];
  computedComplete: boolean;
  readyForReview: boolean;
}

/* ── styles ─────────────────────────────────────────────────────── */

const Wrapper = styled('div', {
  width: '100%',
  maxWidth: '1100px',
  height: '100%',
  overflowY: 'auto',
  padding: '0 1rem 3rem',
  boxSizing: 'border-box',
});

const PageTitle = styled('h1', {
  fontSize: '1.5rem',
  margin: '0 0 0.25rem',
  color: '$sand12',
});

const PageSubtitle = styled('p', {
  fontSize: '0.875rem',
  color: '$sand11',
  margin: '0 0 1.5rem',
});

const SectionHeader = styled('h2', {
  fontSize: '1.05rem',
  margin: '2rem 0 0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '$sand12',
});

const CountBadge = styled('span', {
  fontSize: '0.75rem',
  fontWeight: 700,
  borderRadius: '999px',
  padding: '0.1rem 0.55rem',
  variants: {
    tone: {
      amber: { background: '$yellow4', color: '$yellow11' },
      green: { background: '$green4', color: '$green11' },
      grey: { background: '$sand4', color: '$sand11' },
    },
  },
  defaultVariants: { tone: 'grey' },
});

const Card = styled('div', {
  border: '1px solid $sand6',
  borderRadius: '0.75rem',
  background: 'white',
  padding: '1rem',
  marginBottom: '0.75rem',
  display: 'flex',
  gap: '1rem',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
});

const Photo = styled('img', {
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid $green8',
  flexShrink: 0,
});

const PhotoPlaceholder = styled('div', {
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  background: '$sand4',
  color: '$sand10',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.65rem',
  textAlign: 'center',
  flexShrink: 0,
});

const Info = styled('div', {
  flex: 1,
  minWidth: '260px',
});

const Name = styled('div', {
  fontWeight: 700,
  fontSize: '1rem',
  color: '$sand12',
});

const Email = styled('div', {
  fontSize: '0.8rem',
  color: '$sand11',
  marginBottom: '0.35rem',
});

const Credentials = styled('div', {
  fontSize: '0.85rem',
  color: '$sand12',
  marginBottom: '0.5rem',
});

const ChipRow = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.35rem',
  marginTop: '0.4rem',
});

const Chip = styled('span', {
  fontSize: '0.7rem',
  fontWeight: 600,
  borderRadius: '999px',
  padding: '0.15rem 0.55rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  variants: {
    tone: {
      green: { background: '$green3', color: '$green11', border: '1px solid $green7' },
      amber: { background: '$yellow2', color: '$yellow11', border: '1px solid $yellow8' },
      red: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
      grey: { background: '$sand3', color: '$sand11', border: '1px solid $sand6' },
    },
  },
  defaultVariants: { tone: 'grey' },
});

const Actions = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  minWidth: '170px',
});

const ActionButton = styled('button', {
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.4rem',
  padding: '0.45rem 0.75rem',
  borderRadius: '0.5rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center',
  variants: {
    tone: {
      primary: {
        background: '$green9',
        color: 'white',
        '&:hover': { background: '$green10' },
      },
      danger: {
        border: '1px solid #fecaca',
        color: '#b91c1c',
        background: 'white',
        '&:hover': { background: '#fef2f2' },
      },
      neutral: {
        border: '1px solid $sand7',
        color: '$sand12',
        background: 'white',
        '&:hover': { background: '$sand3' },
      },
    },
  },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

const EmptyNote = styled('p', {
  fontSize: '0.85rem',
  color: '$sand10',
  fontStyle: 'italic',
  margin: '0.5rem 0',
});

/* ── page ───────────────────────────────────────────────────────── */

export default function AdminReviewPage() {
  const { userDetails } = useUser();
  const axios = useContext(AxiosContext);
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { scale } = useScale();
  const [previewTutor, setPreviewTutor] = useState<AdminTutor | null>(null);

  const { data: tutors = [], isLoading, isError } = useQuery<AdminTutor[]>({
    queryKey: ['adminTutors'],
    enabled: !!userDetails?.isAdmin,
    queryFn: async () => {
      const { data } = await axios.get(endpoints.adminTutors);
      // A misconfigured rewrite returns the SPA's index.html with a 200 —
      // surface that as a query error instead of crashing on data.filter.
      if (!Array.isArray(data)) throw new Error('Unexpected non-JSON response from /admin/tutors');
      return data;
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async ({ id, verified }: { id: string; verified: boolean }) =>
      (await axios.patch(endpoints.adminTutorVerification(id), { verified })).data,
    onSuccess: (updated: AdminTutor, { verified }) => {
      queryClient.setQueryData<AdminTutor[]>(['adminTutors'], (prev) =>
        (prev || []).map((t) => (t.id === updated.id ? updated : t)),
      );
      addToast({
        variant: 'success',
        title: verified
          ? `${updated.name} approved — now live in search`
          : `${updated.name}'s verification revoked`,
      });
    },
    onError: () => {
      addToast({ variant: 'error', title: 'Failed to update verification' });
    },
  });

  const groups = useMemo(() => {
    const ready = tutors.filter((t) => t.readyForReview);
    const live = tutors.filter((t) => t.account_verified);
    const incomplete = tutors.filter((t) => !t.readyForReview && !t.account_verified);
    return { ready, live, incomplete };
  }, [tutors]);

  // The preview renders the same TutorDetailView parents see. Mirroring the
  // tutor's expertise onto the stand-in parent makes every subject/level they
  // claim show up in the comparison grid instead of "no subjects provided".
  const previewParent = useMemo(() => {
    if (!previewTutor) return null;
    return {
      ...userDetails,
      expertise: previewTutor.expertise ?? {},
      subjects: [],
      availability: undefined,
    } as unknown as UserDetails;
  }, [previewTutor, userDetails]);

  if (!userDetails?.isAdmin) {
    return <Navigate to="/not-found" replace />;
  }

  const handleVerify = (tutor: AdminTutor, verified: boolean) => {
    const action = verified
      ? `Approve ${tutor.name}? They will appear in search results and be notified by email.`
      : `Revoke ${tutor.name}'s verification? They will disappear from search results.`;
    if (window.confirm(action)) {
      verifyMutation.mutate({ id: tutor.id, verified });
    }
  };

  const renderTutorCard = (tutor: AdminTutor) => {
    const credentials = [
      tutor.university,
      tutor.college,
      tutor.degreeCourse,
      tutor.gradYear && `Class of ${tutor.gradYear}`,
      tutor.classification,
    ]
      .filter(Boolean)
      .join(' • ');

    const flagStale = !!tutor.isProfileComplete !== tutor.computedComplete;
    const isMutating =
      verifyMutation.isPending && verifyMutation.variables?.id === tutor.id;

    return (
      <Card key={tutor.id}>
        {tutor.profilePhotoUrl ? (
          <Photo src={tutor.profilePhotoUrl} alt={tutor.name || 'Tutor'} />
        ) : (
          <PhotoPlaceholder>No photo</PhotoPlaceholder>
        )}

        <Info>
          <Name>{tutor.name}</Name>
          <Email>{tutor.email}</Email>
          {credentials && <Credentials>{credentials}</Credentials>}
          {tutor.hourlyRate != null && (
            <Credentials>£{tutor.hourlyRate}/hr (tutor's rate, before fees)</Credentials>
          )}

          <ChipRow>
            <Chip tone={tutor.email_verified ? 'green' : 'red'}>
              Email {tutor.email_verified ? 'verified' : 'unverified'}
            </Chip>
            <Chip tone={tutor.account_verified ? 'green' : 'grey'}>
              {tutor.account_verified ? 'Live in search' : 'Not approved'}
            </Chip>
            {tutor.discoverable === false && <Chip tone="grey">Hidden (not discoverable)</Chip>}
            {flagStale && (
              <Chip tone="red">
                <ExclamationTriangleFill size={10} />
                Stale DB flag: isProfileComplete = {String(!!tutor.isProfileComplete)}
              </Chip>
            )}
          </ChipRow>

          {tutor.missingFields.length > 0 && (
            <ChipRow>
              {tutor.missingFields.map((field) => (
                <Chip key={field} tone="amber">
                  Missing: {field}
                </Chip>
              ))}
            </ChipRow>
          )}
        </Info>

        <Actions>
          <ActionButton tone="neutral" onClick={() => setPreviewTutor(tutor)}>
            <EyeFill size={14} /> Preview profile
          </ActionButton>
          <ActionButton
            tone="neutral"
            disabled={!tutor.proofFileUrl}
            onClick={() => tutor.proofFileUrl && window.open(tutor.proofFileUrl, '_blank', 'noopener')}
          >
            <FileEarmarkTextFill size={14} />
            {tutor.proofFileUrl ? 'View proof of degree' : 'No proof uploaded'}
          </ActionButton>
          <ActionButton
            tone="neutral"
            disabled={!tutor.CVFileUrl}
            onClick={() => tutor.CVFileUrl && window.open(tutor.CVFileUrl, '_blank', 'noopener')}
          >
            <FilePersonFill size={14} />
            {tutor.CVFileUrl ? 'View CV' : 'No CV uploaded'}
          </ActionButton>
          {tutor.account_verified ? (
            <ActionButton tone="danger" disabled={isMutating} onClick={() => handleVerify(tutor, false)}>
              <XCircleFill size={14} /> Revoke verification
            </ActionButton>
          ) : (
            <ActionButton tone="primary" disabled={isMutating} onClick={() => handleVerify(tutor, true)}>
              <CheckCircleFill size={14} /> Approve tutor
            </ActionButton>
          )}
        </Actions>
      </Card>
    );
  };

  return (
    <>
      <Helmet><title>Admin — tutor review - tute</title></Helmet>
      <PageShell css={{ height: `calc(100dvh / ${scale})`, position: 'relative', paddingLeft: '1rem' }}>
        <AuthSkyline light />
        <Wrapper>
          <PageTitle>Tutor review</PageTitle>
          <PageSubtitle>
            Everything a tutor has uploaded, exactly as parents would see it. Approve to make
            them visible in search; revoke to pull them back out.
          </PageSubtitle>

          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <ClipLoader size={28} />
            </div>
          )}
          {isError && <EmptyNote>Failed to load tutors — are you logged in as the admin account?</EmptyNote>}

          {!isLoading && !isError && (
            <>
              <SectionHeader>
                Ready for review <CountBadge tone="amber">{groups.ready.length}</CountBadge>
              </SectionHeader>
              {groups.ready.length === 0 && <EmptyNote>No tutors waiting for review.</EmptyNote>}
              {groups.ready.map(renderTutorCard)}

              <SectionHeader>
                Live in search <CountBadge tone="green">{groups.live.length}</CountBadge>
              </SectionHeader>
              {groups.live.length === 0 && <EmptyNote>No verified tutors yet.</EmptyNote>}
              {groups.live.map(renderTutorCard)}

              <SectionHeader>
                Incomplete profiles <CountBadge>{groups.incomplete.length}</CountBadge>
              </SectionHeader>
              {groups.incomplete.length === 0 && <EmptyNote>No incomplete profiles.</EmptyNote>}
              {groups.incomplete.map(renderTutorCard)}
            </>
          )}
        </Wrapper>
      </PageShell>

      {previewTutor && previewParent && createPortal(
        <Overlay onClick={() => setPreviewTutor(null)} style={{ zIndex: 9999 }}>
          <Dialog
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '900px', width: '90%', height: '90vh', overflow: 'hidden', padding: 0, background: 'transparent', boxShadow: 'none', display: 'flex' }}
          >
            <TutorDetailView
              tutor={previewTutor}
              parent={previewParent}
              onClose={() => setPreviewTutor(null)}
              showActions={false}
            />
          </Dialog>
        </Overlay>,
        document.body,
      )}
    </>
  );
}
